// The egress crossings log — provenance for every outbound send you released at a gate.
//
// This slice STUBS the actual send: the real network crossing runs later in the general Diwa
// browser extension, in the keeper's own session. What we do NOW is record the intent — the
// channel, recipient, message preview, and the mission it came from — as evidence, the moment
// you approve at the gate. That record surfaces on the Agents page beside Tala's ledger, so
// every border crossing is logged and readable.
//
// Client-side + localStorage-backed on purpose: egress custody is not yet wired server-side
// (the CDS backend still refuses to grant egress), so the queue lives with the keeper until
// the extension lands. Guarded so it degrades to in-memory outside the browser (tests/SSR).

export interface EgressCrossing {
  id: string
  /** ISO timestamp of the release. */
  at: string
  channel: string
  to: string
  subject?: string
  /** A short preview of the body — evidence, not the full payload. */
  preview: string
  missionId: string
  missionTitle: string
  step: string
  /** 'queued' — handed to your extension, not yet sent. The only state this stub knows. */
  status: 'queued'
}

const KEY = 'diwa-egress-log'
/** Dispatched on any change so an open Agents tab can refresh its crossings log. */
export const EGRESS_EVENT = 'diwa-egress-changed'

let memory: EgressCrossing[] = []

function hasStorage(): boolean {
  try {
    return typeof localStorage !== 'undefined'
  } catch {
    return false
  }
}

function read(): EgressCrossing[] {
  if (!hasStorage()) return memory
  try {
    const raw = localStorage.getItem(KEY)
    return raw ? (JSON.parse(raw) as EgressCrossing[]) : []
  } catch {
    return []
  }
}

function write(list: EgressCrossing[]): void {
  if (hasStorage()) {
    try {
      localStorage.setItem(KEY, JSON.stringify(list))
    } catch {
      /* quota / private mode — fall through to the event so the UI still updates */
    }
  } else {
    memory = list
  }
  try {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(EGRESS_EVENT))
  } catch {
    /* no window (tests) — nothing to notify */
  }
}

/** Newest crossing first. */
export function listCrossings(): EgressCrossing[] {
  return read()
    .slice()
    .sort((a, b) => (a.at < b.at ? 1 : -1))
}

/** Log a release — called from the mission gate when you approve & queue an outbound send. */
export function recordCrossing(
  input: Omit<EgressCrossing, 'id' | 'at' | 'status'>,
): EgressCrossing {
  const crossing: EgressCrossing = {
    ...input,
    id: `egr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`,
    at: new Date().toISOString(),
    status: 'queued',
  }
  write([crossing, ...read()])
  return crossing
}
